import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTimelineContext } from '@components/MessageTimeline/hooks/useTimelineContext'
import { useConnectedNonPrivyWallets } from '@components/Web3/ConnectWallet/useConnectedNonPrivyWallets'
import { usePrivyConnectWallet } from '@components/Web3/ConnectWallet/usePrivyConnectWallet'
import { UserDevice } from '@towns-protocol/encryption'
import {
    InteractionRequestPayload_Form,
    InteractionRequestPayload_Form_Component,
    InteractionRequestPayload_Signature,
    InteractionRequestPayload_Signature_SignatureType,
    InteractionRequestPayload_Signature_SignatureValidation,
    InteractionRequestPayload_Transaction,
    InteractionResponsePayload,
    PlainMessage,
} from '@towns-protocol/proto'
import { genIdBlob, InteractionRequestEvent, isDefined, TimelineEvent } from '@towns-protocol/sdk'
import { Box, Icon, Stack, Text } from '@ui'
import { providers } from 'ethers'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useEnvironment } from 'hooks/useEnvironment'
import { type GetSigner, WalletReady } from 'privy/WalletReady'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import {
    SignatureApprovalError,
    type SignatureApprovalRequest,
    useSignatureApprovalExecutor,
} from 'signature'
import { useBaseWrites, useMyLinkedWallets, useTownsClient } from 'use-towns-client'
import { Address, hexToBytes } from 'viem'
import { shortAddress } from 'workers/utils'

import {
    MessageCard,
    MessageFormButton,
    MessageFormInputContainer,
    MessageFormSelect,
    MessageFormTextField,
} from './form/MessageForm'
import { DATA_ATTR_INTERACTION_REQUEST } from './constants'
import { EVMTransactionPreview } from './EVMTransactionPreview'

const getWeb3ErrorMessage = (error: unknown): string => {
    const message = error instanceof Error ? error.message : String(error)

    if (message.includes('INSUFFICIENT_FUNDS') || message.includes('insufficient funds')) {
        return 'Insufficient funds'
    }
    if (message.includes('user rejected') || message.includes('User rejected')) {
        return 'Transaction cancelled'
    }
    if (message.includes('nonce')) {
        return 'Transaction conflict - please try again'
    }
    if (message.includes('Unsupported chainId')) {
        return 'Unsupported network'
    }
    return 'Transaction failed'
}

type Props = {
    event: TimelineEvent
    request: InteractionRequestEvent
}

export const InteractionRequestItem = ({ event, request }: Props) => {
    // Wrapper with data attribute to prevent message modal sheet from opening on touch devices
    // See MessageLayout.tsx onClick handler
    const content = (() => {
        switch (request.payload?.content.case) {
            case 'signature':
                return (
                    <SignatureRequestItem
                        request={request.payload.content.value}
                        recipientUserId={event.sender.id}
                        recipientDevice={request.payload.encryptionDevice}
                        threadParentId={request.threadParentId}
                    />
                )

            case 'form':
                return (
                    <FormRequestItem
                        request={request.payload.content.value}
                        recipientUserId={event.sender.id}
                        recipientDevice={request.payload.encryptionDevice}
                        threadParentId={request.threadParentId}
                    />
                )
            case 'transaction':
                return (
                    <TransactionRequestItem
                        request={request.payload.content.value}
                        recipientUserId={event.sender.id}
                        recipientDevice={request.payload.encryptionDevice}
                        threadParentId={request.threadParentId}
                    />
                )
            default:
                return (
                    <Box>
                        {'Unknown interaction request type: ' + request.payload?.content.case}
                    </Box>
                )
        }
    })()

    return <Box {...{ [DATA_ATTR_INTERACTION_REQUEST]: true }}>{content}</Box>
}

const SignatureRequestItem = ({
    request,
    recipientUserId,
    recipientDevice,
    threadParentId,
}: {
    request: PlainMessage<InteractionRequestPayload_Signature>
    recipientUserId: string
    recipientDevice: UserDevice | undefined
    threadParentId: string | undefined
}) => {
    const { data: _linkedWallets } = useMyLinkedWallets()
    const { data: abstractAccountAddress } = useMyAbstractAccountAddress()
    const { wallets: connectedWallets } = useConnectedNonPrivyWallets()
    const { userOpsInstance } = useBaseWrites()
    const { openPanel } = usePanelActions()
    const [isSigning, setIsSigning] = useState(false)
    const [error, setError] = useState<string | undefined>()
    const [signatureComplete, setSignatureComplete] = useState(false)

    const { channelId } = useTimelineContext()
    const client = useTownsClient()
    const { executeSignature } = useSignatureApprovalExecutor({
        connectedWallets,
        userOpsInstance,
    })

    // if the signature validation is EIP712_ONLY, we don't allow EIP1271 wallets
    const allowEIP1271 =
        request.signatureValidation !==
        InteractionRequestPayload_Signature_SignatureValidation.EIP712_ONLY

    const linkedWallets = useMemo(() => {
        if (allowEIP1271) {
            return _linkedWallets
        }
        return _linkedWallets?.filter(
            (w) => w.toLowerCase() !== abstractAccountAddress?.toLowerCase(),
        )
    }, [_linkedWallets, abstractAccountAddress, allowEIP1271])

    // If a specific signer wallet is required, use that; otherwise use first available wallet
    const requiredSignerWallet = request.signerWallet
        ? (request.signerWallet as Address)
        : undefined

    const initialWallet = requiredSignerWallet || (linkedWallets?.[0] as Address | undefined)

    const [selectedWallet, setSelectedWallet] = useState<Address | undefined>(initialWallet)

    // Update selected wallet when linkedWallets loads
    useEffect(() => {
        if (!selectedWallet && linkedWallets && linkedWallets.length > 0) {
            setSelectedWallet(linkedWallets[0] as Address)
        }
    }, [linkedWallets, selectedWallet])

    const connectWallet = usePrivyConnectWallet({
        onSuccess: () => {
            console.log('Wallet connected')
        },
        onError: (error) => {
            console.error('Error connecting wallet:', error)
        },
    })

    // Check if required wallet is in linked wallets
    const hasRequiredWallet = requiredSignerWallet
        ? (linkedWallets?.some((w) => w.toLowerCase() === requiredSignerWallet.toLowerCase()) ??
          false)
        : false

    // Check if selected wallet is abstract account
    const isAbstractAccount = useMemo(() => {
        return (
            selectedWallet?.toLowerCase() === abstractAccountAddress?.toLowerCase() &&
            abstractAccountAddress !== undefined
        )
    }, [selectedWallet, abstractAccountAddress])

    // Check if the selected wallet is connected
    const selectedWalletConnected = useMemo(() => {
        if (isAbstractAccount) {
            return true // Always connected via root key
        }
        return connectedWallets.some(
            (w) => w.address.toLowerCase() === selectedWallet?.toLowerCase(),
        )
    }, [isAbstractAccount, connectedWallets, selectedWallet])

    const onClickLinkWallet = useCallback(() => {
        openPanel('wallets')
    }, [openPanel])

    const handleSign = useCallback(
        async (getRootKeySigner: GetSigner) => {
            if (!selectedWallet) {
                return
            }

            if (!selectedWalletConnected) {
                connectWallet()
                return
            }

            setIsSigning(true)
            setError(undefined)
            try {
                let signatureApprovalRequest: SignatureApprovalRequest
                const chainId = parseInt(request.chainId)

                switch (request.type) {
                    case InteractionRequestPayload_Signature_SignatureType.PERSONAL_SIGN:
                        signatureApprovalRequest = {
                            type: 'personal',
                            payload: request.data,
                            chainId,
                            signerWallet: requiredSignerWallet,
                            allowEIP1271,
                        }
                        break
                    case InteractionRequestPayload_Signature_SignatureType.TYPED_DATA:
                        signatureApprovalRequest = {
                            type: 'typed',
                            payload: request.data,
                            chainId,
                            signerWallet: requiredSignerWallet,
                            allowEIP1271,
                        }
                        break
                    default:
                        setError('Unsupported signature type')
                        return
                }

                const signature = await executeSignature({
                    selectedWallet,
                    abstractAccountAddress,
                    request: signatureApprovalRequest,
                    getRootKeySigner,
                })

                const recipient = hexToBytes(recipientUserId as `0x${string}`)
                const response: PlainMessage<InteractionResponsePayload> = {
                    salt: genIdBlob(),
                    content: {
                        case: 'signature',
                        value: {
                            requestId: request.id,
                            signature,
                        },
                    },
                }

                await client.sendInteractionResponse(
                    channelId,
                    recipient,
                    response,
                    recipientDevice,
                    threadParentId,
                )
                console.log('Signature sent successfully')
                setSignatureComplete(true)
            } catch (error) {
                if (error instanceof SignatureApprovalError) {
                    setError(error.message)
                } else {
                    setError(getWeb3ErrorMessage(error))
                }
                console.error('Error signing:', error)
            } finally {
                setIsSigning(false)
            }
        },
        [
            selectedWallet,
            selectedWalletConnected,
            connectWallet,
            executeSignature,
            abstractAccountAddress,
            recipientUserId,
            client,
            channelId,
            recipientDevice,
            threadParentId,
            request.id,
            request.chainId,
            request.data,
            request.type,
            requiredSignerWallet,
            allowEIP1271,
        ],
    )

    return (
        <WalletReady>
            {({ getSigner }) => (
                <MessageCard
                    title={request.title}
                    subtitle={
                        linkedWallets && linkedWallets.length > 0 ? request.subtitle : undefined
                    }
                >
                    {signatureComplete ? (
                        <Stack
                            horizontal
                            padding
                            gap="sm"
                            background="level3"
                            borderRadius="sm"
                            border="level4"
                            alignItems="center"
                        >
                            <Icon type="check" size="square_inline" color="positive" />
                            <Text color="positive">Signed</Text>
                        </Stack>
                    ) : (
                        <>
                            {!linkedWallets || linkedWallets?.length === 0 ? (
                                <Stack gap>
                                    <Text>
                                        You don&apos;t have any wallets linked. Link a wallet to
                                        continue.
                                    </Text>
                                    <MessageFormInputContainer>
                                        <MessageFormButton onClick={onClickLinkWallet}>
                                            Link a wallet
                                        </MessageFormButton>
                                    </MessageFormInputContainer>
                                </Stack>
                            ) : requiredSignerWallet ? (
                                hasRequiredWallet ? (
                                    <MessageFormButton
                                        disabled={isSigning}
                                        onClick={() => handleSign(getSigner)}
                                    >
                                        {isSigning
                                            ? 'Signing...'
                                            : `Sign with ${shortAddress(requiredSignerWallet)}`}
                                    </MessageFormButton>
                                ) : (
                                    <Box
                                        centerContent
                                        padding="sm"
                                        background="level1"
                                        borderRadius="sm"
                                    >
                                        <Text color="negative">
                                            Required wallet not linked:{' '}
                                            {`${shortAddress(requiredSignerWallet)}`}
                                        </Text>
                                    </Box>
                                )
                            ) : (
                                <MessageFormInputContainer>
                                    <MessageFormSelect
                                        icon="wallet"
                                        defaultValue={selectedWallet}
                                        options={linkedWallets.map((wallet) => {
                                            const isSmartAccount =
                                                wallet.toLowerCase() ===
                                                abstractAccountAddress?.toLowerCase()
                                            return {
                                                label: `${shortAddress(wallet)}${isSmartAccount ? ' (Towns Wallet)' : ''}`,
                                                value: wallet,
                                            }
                                        })}
                                        onChange={(value) => setSelectedWallet(value as Address)}
                                    />
                                    <MessageFormButton
                                        disabled={!selectedWallet || isSigning}
                                        onClick={() => handleSign(getSigner)}
                                    >
                                        {isSigning
                                            ? 'Signing...'
                                            : selectedWalletConnected
                                              ? 'Sign with selected wallet'
                                              : 'Connect wallet to sign'}
                                    </MessageFormButton>
                                </MessageFormInputContainer>
                            )}
                            {error && (
                                <Text color="negative" fontSize="sm">
                                    {error}
                                </Text>
                            )}
                        </>
                    )}
                </MessageCard>
            )}
        </WalletReady>
    )
}

const FormRequestItem = ({
    request,
    recipientUserId,
    recipientDevice,
    threadParentId,
}: {
    request: PlainMessage<InteractionRequestPayload_Form>
    recipientUserId: string
    recipientDevice: UserDevice | undefined
    threadParentId: string | undefined
}) => {
    const [textInputValues, setTextInputValues] = useState<Record<string, string>>({})
    const [submittingButtonId, setSubmittingButtonId] = useState<string | null>(null)
    const [submitComplete, setSubmitComplete] = useState(false)
    const [error, setError] = useState<string | undefined>()

    const { channelId } = useTimelineContext()
    const client = useTownsClient()

    const handleTextInputChange = (componentId: string, value: string) => {
        setTextInputValues((prev) => ({
            ...prev,
            [componentId]: value,
        }))
    }

    const handleButtonClick = useCallback(
        async (componentId: string) => {
            setSubmittingButtonId(componentId)
            setError(undefined)

            try {
                // Build the response components based on current form state
                const responseComponents = request.components
                    .map((component) => {
                        if (component.component.case === 'button') {
                            // Button response - only include if it's the clicked button
                            if (component.id === componentId) {
                                return {
                                    id: component.id,
                                    component: {
                                        case: 'button' as const,
                                        value: {},
                                    },
                                }
                            }
                            return null
                        } else if (component.component.case === 'textInput') {
                            // Text input response - include ALL text input values
                            return {
                                id: component.id,
                                component: {
                                    case: 'textInput' as const,
                                    value: {
                                        value: textInputValues[component.id] || '',
                                    },
                                },
                            }
                        }
                        return null
                    })
                    .filter(isDefined)

                // Send form response back to bot
                const recipient = hexToBytes(recipientUserId as `0x${string}`)
                const response: PlainMessage<InteractionResponsePayload> = {
                    salt: genIdBlob(),
                    content: {
                        case: 'form',
                        value: {
                            requestId: request.id,
                            components: responseComponents,
                        },
                    },
                }

                await client.sendInteractionResponse(
                    channelId,
                    recipient,
                    response,
                    recipientDevice,
                    threadParentId,
                )
                setSubmitComplete(true)
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : String(error)
                setError('Error submitting form: ' + errorMessage)
                console.error('Error submitting form:', error)
            } finally {
                setSubmittingButtonId(null)
            }
        },
        [
            request.components,
            request.id,
            recipientUserId,
            client,
            channelId,
            recipientDevice,
            textInputValues,
            threadParentId,
        ],
    )

    const inputComponents = request.components.filter(
        (c) => c.component.case !== 'button',
    ) as InteractionRequestPayload_Form_Component[]

    const actionComponents = request.components.filter(
        (c) => c.component.case === 'button',
    ) as InteractionRequestPayload_Form_Component[]

    const mapComponent = useCallback(
        (component: InteractionRequestPayload_Form_Component) => {
            switch (component.component.case) {
                case 'button': {
                    const isThisButtonSubmitting = submittingButtonId === component.id
                    const isAnyButtonSubmitting = submittingButtonId !== null

                    return (
                        <MessageFormButton
                            key={component.id}
                            disabled={isAnyButtonSubmitting}
                            onClick={() => handleButtonClick(component.id)}
                        >
                            {isThisButtonSubmitting
                                ? 'Submitting...'
                                : component.component.value.label}
                        </MessageFormButton>
                    )
                }
                case 'textInput':
                    return (
                        <MessageFormTextField
                            key={component.id}
                            placeholder={component.component.value.placeholder}
                            value={textInputValues[component.id] || ''}
                            disabled={submittingButtonId !== null}
                            onChange={(e) => handleTextInputChange(component.id, e.target.value)}
                        />
                    )
                default:
                    return null
            }
        },
        [textInputValues, submittingButtonId, handleButtonClick],
    )

    return (
        <MessageCard title={request.title} subtitle={request.subtitle}>
            {submitComplete ? (
                <Stack
                    horizontal
                    padding
                    gap="sm"
                    background="level3"
                    borderRadius="sm"
                    border="level4"
                    alignItems="center"
                >
                    <Icon type="check" size="square_inline" color="positive" />
                    <Text color="positive">Submitted</Text>
                </Stack>
            ) : (
                <Stack gap="paragraph">
                    {inputComponents.length ? (
                        <MessageFormInputContainer>
                            {inputComponents.map(mapComponent)}
                        </MessageFormInputContainer>
                    ) : null}
                    {actionComponents.length ? (
                        <MessageFormInputContainer>
                            {actionComponents.map(mapComponent)}
                        </MessageFormInputContainer>
                    ) : null}
                    {error && (
                        <Text color="negative" fontSize="sm">
                            {error}
                        </Text>
                    )}
                </Stack>
            )}
        </MessageCard>
    )
}

const TransactionRequestItem = ({
    request,
    recipientUserId,
    recipientDevice,
    threadParentId,
}: {
    request: PlainMessage<InteractionRequestPayload_Transaction>
    recipientUserId: string
    recipientDevice: UserDevice | undefined
    threadParentId: string | undefined
}) => {
    const { data: linkedWallets } = useMyLinkedWallets()
    const { data: abstractAccountAddress } = useMyAbstractAccountAddress()
    const { wallets: connectedWallets } = useConnectedNonPrivyWallets()
    const { userOpsInstance } = useBaseWrites()
    const environment = useEnvironment()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | undefined>()
    const [submitComplete, setSubmitComplete] = useState(false)
    const [txHash, setTxHash] = useState<string | undefined>()

    const { channelId } = useTimelineContext()
    const client = useTownsClient()

    // If a specific signer wallet is required, use that; otherwise use first available wallet
    const requiredSignerWallet = request.content.value?.signerWallet
        ? (request.content.value.signerWallet as Address)
        : undefined

    // Filter out abstract account address from linked and connected wallets
    const filteredLinkedWallets = useMemo(() => {
        if (!abstractAccountAddress || !linkedWallets) {
            return linkedWallets
        }
        return linkedWallets.filter(
            (wallet) => wallet.toLowerCase() !== abstractAccountAddress.toLowerCase(),
        )
    }, [linkedWallets, abstractAccountAddress])

    const filteredConnectedWallets = useMemo(() => {
        if (!abstractAccountAddress) {
            return connectedWallets
        }
        return connectedWallets.filter(
            (wallet) => wallet.address.toLowerCase() !== abstractAccountAddress.toLowerCase(),
        )
    }, [connectedWallets, abstractAccountAddress])

    // All available wallets (EOA + abstract account)
    const allWallets = useMemo(() => {
        const wallets: Address[] = []
        if (filteredLinkedWallets) {
            wallets.push(...(filteredLinkedWallets as Address[]))
        }
        // Only include abstract account if chain ID matches base chain
        if (abstractAccountAddress && request.content.value) {
            const requestedChainId = parseInt(request.content.value.chainId)
            if (requestedChainId === environment.baseChain.id) {
                wallets.push(abstractAccountAddress)
            }
        }
        return wallets
    }, [
        filteredLinkedWallets,
        abstractAccountAddress,
        request.content.value,
        environment.baseChain.id,
    ])

    const initialWallet = requiredSignerWallet || allWallets[0]
    const [selectedWallet, setSelectedWallet] = useState<Address | undefined>(initialWallet)

    // Update selected wallet when allWallets loads
    useEffect(() => {
        if (!selectedWallet && allWallets && allWallets.length > 0) {
            setSelectedWallet(allWallets[0] as Address)
        }
    }, [allWallets, selectedWallet])

    const connectWallet = usePrivyConnectWallet({
        onSuccess: () => {
            console.log('Wallet connected')
        },
        onError: (error) => {
            console.error('Error connecting wallet:', error)
        },
    })

    // Check if required wallet is in allWallets
    const hasRequiredWallet = requiredSignerWallet
        ? (allWallets?.some((w) => w.toLowerCase() === requiredSignerWallet.toLowerCase()) ?? false)
        : false

    // Check if selected wallet is abstract account
    const isAbstractAccount = useMemo(() => {
        return (
            selectedWallet?.toLowerCase() === abstractAccountAddress?.toLowerCase() &&
            abstractAccountAddress !== undefined
        )
    }, [selectedWallet, abstractAccountAddress])

    // Check if the selected wallet is connected
    const selectedWalletConnected = useMemo(() => {
        if (isAbstractAccount) {
            // Abstract account is always "connected" via embedded wallet
            return true
        }
        return filteredConnectedWallets.some(
            (w) => w.address.toLowerCase() === selectedWallet?.toLowerCase(),
        )
    }, [isAbstractAccount, filteredConnectedWallets, selectedWallet])

    const handleSubmit = useCallback(
        async (getSigner: GetSigner) => {
            if (!selectedWallet) {
                return
            }

            if (!request.content.value) {
                setError('Invalid transaction request')
                return
            }

            // If wallet is not connected, prompt to connect
            if (!selectedWalletConnected) {
                connectWallet()
                return
            }

            setIsSubmitting(true)
            setError(undefined)

            try {
                const txRequest = request.content.value
                const requestedChainId = parseInt(txRequest.chainId)

                let transactionHash: string

                if (isAbstractAccount) {
                    // Path A: Abstract Account - Use UserOperation
                    if (!userOpsInstance) {
                        throw new Error('UserOps instance not available')
                    }

                    // Get the root key signer for abstract account
                    const signer = await getSigner()
                    if (!signer) {
                        throw new Error('Failed to get signer')
                    }

                    const smartAccount = await userOpsInstance?.smartAccountSession.getSmartAccount(
                        {
                            signer: signer,
                        },
                    )

                    const result = await smartAccount.sendUserOp({
                        toAddress: txRequest.to as Address,
                        callData: txRequest.data as `0x${string}`,
                        value:
                            txRequest.value && txRequest.value !== '0x' && txRequest.value !== ''
                                ? BigInt(txRequest.value)
                                : 0n,
                        signer,
                        spaceId: undefined,
                        functionHashForPaymasterProxy: 'unsponsored',
                    })

                    // Wait for the UserOp receipt
                    const receipt = await result.getUserOperationReceipt()
                    if (!receipt) {
                        throw new Error('Failed to get transaction receipt')
                    }
                    if (!receipt.success) {
                        throw new Error('Transaction failed')
                    }

                    transactionHash = receipt.receipt.transactionHash
                } else {
                    // Path B: EOA Wallet - Regular Transaction
                    const connectedWallet = filteredConnectedWallets.find(
                        (w) => w.address.toLowerCase() === selectedWallet.toLowerCase(),
                    )
                    if (!connectedWallet) {
                        setError('Wallet not connected')
                        return
                    }

                    // Check if we need to switch chains
                    let privyProvider = await connectedWallet.getEthereumProvider()
                    let provider = new providers.Web3Provider(privyProvider)
                    const currentNetwork = await provider.getNetwork()

                    if (currentNetwork.chainId !== requestedChainId) {
                        try {
                            await privyProvider.request({
                                method: 'wallet_switchEthereumChain',
                                params: [{ chainId: `0x${requestedChainId.toString(16)}` }],
                            })
                            // Recreate provider after chain switch
                            privyProvider = await connectedWallet.getEthereumProvider()
                            provider = new providers.Web3Provider(privyProvider)
                        } catch {
                            setError('Chain switch failed')
                            return
                        }
                    }

                    const signer = provider.getSigner()

                    // Send regular transaction
                    const tx = await signer.sendTransaction({
                        to: txRequest.to,
                        value:
                            txRequest.value && txRequest.value !== '0x' && txRequest.value !== ''
                                ? BigInt(txRequest.value)
                                : 0n,
                        data: txRequest.data,
                    })

                    // Wait for confirmation
                    const receipt = await tx.wait()
                    if (!receipt) {
                        throw new Error('Failed to get transaction receipt')
                    }

                    transactionHash = receipt.transactionHash
                }

                // Send interaction response back to bot
                const recipient = hexToBytes(recipientUserId as `0x${string}`)
                const response: PlainMessage<InteractionResponsePayload> = {
                    salt: genIdBlob(),
                    content: {
                        case: 'transaction',
                        value: {
                            requestId: request.id,
                            txHash: transactionHash,
                        },
                    },
                }

                await client.sendInteractionResponse(
                    channelId,
                    recipient,
                    response,
                    recipientDevice,
                    threadParentId,
                )

                setTxHash(transactionHash)
                setSubmitComplete(true)
            } catch (error) {
                setError(getWeb3ErrorMessage(error))
                console.error('Error submitting transaction:', error)
            } finally {
                setIsSubmitting(false)
            }
        },
        [
            selectedWallet,
            request,
            selectedWalletConnected,
            connectWallet,
            isAbstractAccount,
            userOpsInstance,
            filteredConnectedWallets,
            recipientUserId,
            client,
            channelId,
            recipientDevice,
            threadParentId,
        ],
    )

    if (!request.content.value) {
        return <Box>Invalid transaction request</Box>
    }

    const txRequest = request.content.value
    return (
        <WalletReady>
            {({ getSigner }) => (
                <MessageCard
                    title={request.title}
                    subtitle={linkedWallets ? request.subtitle : undefined}
                >
                    {submitComplete ? (
                        <Stack
                            horizontal
                            padding
                            gap="sm"
                            background="level3"
                            borderRadius="sm"
                            border="level4"
                            alignItems="center"
                        >
                            <Icon type="check" size="square_inline" color="positive" />
                            <Stack gap="xs">
                                <Text color="positive">Transaction Submitted</Text>
                                {txHash && (
                                    <Text fontSize="sm">
                                        {txHash.slice(0, 10)}...{txHash.slice(-8)}
                                    </Text>
                                )}
                            </Stack>
                        </Stack>
                    ) : (
                        <>
                            <EVMTransactionPreview
                                chainId={txRequest.chainId}
                                to={txRequest.to}
                                value={txRequest.value}
                                data={txRequest.data}
                            />

                            {!allWallets || allWallets.length === 0 ? (
                                <Box
                                    centerContent
                                    padding="sm"
                                    background="level1"
                                    borderRadius="sm"
                                >
                                    <Text color="negative">No wallets available</Text>
                                </Box>
                            ) : requiredSignerWallet ? (
                                hasRequiredWallet ? (
                                    <MessageFormInputContainer>
                                        <MessageFormButton
                                            disabled={isSubmitting}
                                            onClick={() => handleSubmit(getSigner)}
                                        >
                                            {isSubmitting
                                                ? 'Submitting...'
                                                : requiredSignerWallet.toLowerCase() ===
                                                    abstractAccountAddress?.toLowerCase()
                                                  ? 'Submit with Towns Wallet'
                                                  : `Submit with ${shortAddress(requiredSignerWallet)}`}
                                        </MessageFormButton>
                                    </MessageFormInputContainer>
                                ) : (
                                    <Box
                                        centerContent
                                        padding="sm"
                                        background="level1"
                                        borderRadius="sm"
                                    >
                                        <Text color="negative">
                                            Required wallet not available:{' '}
                                            {`${shortAddress(requiredSignerWallet)}`}
                                        </Text>
                                    </Box>
                                )
                            ) : (
                                <MessageFormInputContainer>
                                    <MessageFormSelect
                                        defaultValue={selectedWallet}
                                        options={allWallets.map((wallet) => {
                                            const isAbstract =
                                                wallet.toLowerCase() ===
                                                abstractAccountAddress?.toLowerCase()
                                            return {
                                                label: `${shortAddress(wallet)}${isAbstract ? ' (Towns Wallet)' : ''}`,
                                                value: wallet,
                                            }
                                        })}
                                        onChange={(value) => setSelectedWallet(value as Address)}
                                    />
                                    <MessageFormButton
                                        disabled={!selectedWallet || isSubmitting}
                                        onClick={() => handleSubmit(getSigner)}
                                    >
                                        {isSubmitting
                                            ? 'Submitting...'
                                            : selectedWalletConnected
                                              ? 'Submit Transaction'
                                              : 'Connect wallet to submit'}
                                    </MessageFormButton>
                                </MessageFormInputContainer>
                            )}
                            {error && (
                                <Text color="negative" fontSize="sm">
                                    {error}
                                </Text>
                            )}
                        </>
                    )}
                </MessageCard>
            )}
        </WalletReady>
    )
}
